import { useCallback } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import Logger from 'utils/logger';
import {
  buildReturnUrl,
  StripeReturnContext,
} from 'app/modules/settings/modules/billing/utils/buildReturnUrl';

export type StripeSubmitInputs = {
  // clientSecret is kept for API compatibility with existing callers; the
  // Payment Element receives it via the Elements provider (a later task), so
  // confirmSetup no longer needs it directly here.
  clientSecret: string;
  billingEmail: string;
  holderName: string;
  /**
   * Optional context carried through the PayPal redirect return_url so the
   * BillingReturnHandler knows which flow to resume on landing:
   *   - `add`    → attach-only (AddStripePaymentMethodModal)
   *   - `signup` → setup-subscription (SetupSubscriptionModal)
   * For card payments this is irrelevant — they resolve inline.
   */
  returnContext?: StripeReturnContext;
};

export type StripeSubmitError =
  | { kind: 'stripe'; message: string }
  | { kind: 'unknown'; message: string };

export type StripeSubmitResult =
  | { ok: true; paymentMethodId: string; setupIntentId: string }
  | { ok: false; error: StripeSubmitError };

/**
 * Confirms the Stripe SetupIntent client-side using the Payment Element and
 * returns the resulting `payment_method.id` (`pm_xxx`). The caller decides
 * what to do next:
 *
 *   - `SetupSubscriptionModal` passes the id as `paymentMethodNonce` to
 *     `POST /billing/setup-subscription`. The BE attaches the PM as part
 *     of creating the subscription; no separate attach call.
 *   - `AddStripePaymentMethodModal` (standalone payment-method-add flow)
 *     calls `POST /billing/payment-method/stripe` explicitly to attach.
 *
 * For card payments, `redirect: 'if_required'` resolves the SetupIntent
 * inline and execution continues normally. For redirect-based methods like
 * PayPal, Stripe.js performs a full-page redirect to `return_url`; the
 * return-handler (Task 14) resumes the flow from the URL parameters.
 *
 * Per handoff §3, the `Idempotency-Key` belongs to the BE endpoint, not
 * to this client-side confirm step — Stripe's SetupIntent already
 * dedupes against the clientSecret.
 */
export function useStripePaymentSubmit() {
  const stripe = useStripe();
  const elements = useElements();

  const submit = useCallback(
    async (inputs: StripeSubmitInputs): Promise<StripeSubmitResult> => {
      if (!stripe || !elements) {
        return {
          ok: false,
          error: { kind: 'unknown', message: '' },
        };
      }

      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: buildReturnUrl(inputs.returnContext),
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        return {
          ok: false,
          error: {
            kind: 'stripe',
            message: confirmError.message ?? '',
          },
        };
      }

      if (!setupIntent || typeof setupIntent.payment_method !== 'string') {
        Logger.error('confirmSetup returned no payment_method id', {
          setupIntentId: setupIntent?.id,
          status: setupIntent?.status,
          hasPaymentMethod: !!setupIntent?.payment_method,
        });
        return {
          ok: false,
          error: { kind: 'unknown', message: '' },
        };
      }

      if (setupIntent.status !== 'succeeded') {
        Logger.error('confirmSetup returned non-succeeded status', {
          setupIntentId: setupIntent.id,
          status: setupIntent.status,
        });
        return {
          ok: false,
          error: {
            kind: 'stripe',
            message: '',
          },
        };
      }

      return {
        ok: true,
        paymentMethodId: setupIntent.payment_method,
        setupIntentId: setupIntent.id,
      };
    },
    [stripe, elements],
  );

  return { submit };
}
